import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

dayjs.extend(customParseFormat); // need this because we receive non-standard date strings from some apis
dayjs.extend(isSameOrBefore);

type DateToParse = string | number | dayjs.Dayjs | Date | null;

export const formatDate = (formatString?: string, date = new Date()) => dayjs(date).format(formatString);
export const parseDate = (dateToParse?: DateToParse, formatString?: dayjs.OptionType) => dayjs(dateToParse, formatString).toDate();
export const addDaysToDateAndFormat = (originalDate: DateToParse, daysToAdd: number, formatString = 'MMM DD, YYYY') =>
  dayjs(originalDate).add(daysToAdd, 'day').format(formatString);
export const isTodayWithinNDaysAfterDate = (originalDate: DateToParse, nDays: number) =>
  dayjs().isSameOrBefore(dayjs(originalDate).add(nDays, 'day'));
export const extractShortMonthDayFromDate = (originalDate?: string) => {
  if (!originalDate) return;
  const parts = originalDate.split(',');
  return parts[0]?.trim();
};
export const parseDateFromMiliseconds = (originalDate: any, noYear: boolean) => {
  const date = new Date(originalDate);

  return noYear
    ? date.toLocaleDateString('en-US', {
        month: 'short',
        day: '2-digit'
      })
    : date.toLocaleDateString('en-US', {
        month: 'short',
        day: '2-digit',
        year: 'numeric'
      });
};
interface isWithinReturnWindowProps {
  orderDate: string;
  windowLength: number;
  forceStoreCreditStartDate?: boolean;
}

interface getReturnStoreCreditStartDateProps {
  windowLength: number;
  forceStoreCreditStartDate?: boolean;
  todayDate?: string;
}

/**
 * Calculates the start date for store credit returns policy
 * @param {Object} params
 * @param {number} params.windowLength - The length of the return window in days
 * @param {boolean} [params.forceStoreCreditStartDate] - Optional override date to force a specific start date
 * @param {string} [params.todayDate] - Optional date to pass to set the todays date
 * @returns {dayjs.Dayjs} Returns a dayjs object representing the store credit policy start date
 * @remarks If forceStoreCreditStartDate is true, returns a date that is (windowLength + 31) days before today.
 * Otherwise, returns the hardcoded date of July 9, 2025.
 * The calculation includes a 1-day buffer for timezone differences.
 * @see {@link https://quip-amazon.com/NPn0ABslTM20/Fellowship-of-the-Store-Credit#temp:C:XaVc8159fab707240fabad84f45f Leadership Timeline}
 * @remarks Only used while the `web.returnpolicy.60day` killswitch is off; the 60-day policy applies
 * the same window to every order, so there is nothing to grandfather.
 */
// TODO FEE-8732: clean up killswitch
export const getReturnStoreCreditStartDate = ({ windowLength, forceStoreCreditStartDate, todayDate }: getReturnStoreCreditStartDateProps) => {
  const today = todayDate ? dayjs(todayDate) : dayjs(); // if want to set specific date for "today"
  const BUFFER_DAYS = 1; // accounts for time-zones, gives an extra day for return
  const oneMonthbeforeWindowLength = today.subtract(windowLength + BUFFER_DAYS + 30, 'day');

  // Leadership Timeline Tracking this date here - https://quip-amazon.com/NPn0ABslTM20/Fellowship-of-the-Store-Credit#temp:C:XaVc8159fab707240fabad84f45f
  return forceStoreCreditStartDate ? oneMonthbeforeWindowLength : dayjs('2025-07-09');
};

/**
 * Determines if an order date is before the store credit return policy start date
 * @param {Object} params
 * @param {string} params.orderDate - The order date string
 * @param {number} params.windowLength - The length of the return window in days
 * @param {boolean} [params.forceStoreCreditStartDate] - Optional override date for store credit policy start
 * @returns {boolean} Returns true if the order date is before the store credit start date
 */
// TODO FEE-8732: clean up killswitch
export const getIsOrderDateBeforeReturnStoreCreditStartDate = ({
  orderDate,
  windowLength,
  forceStoreCreditStartDate
}: isWithinReturnWindowProps) => {
  const parsedOrderDate = dayjs(orderDate.split(' at ')[0]);
  const RETURN_STORE_CREDIT_START_DATE = getReturnStoreCreditStartDate({ windowLength, forceStoreCreditStartDate });

  return parsedOrderDate.isBefore(RETURN_STORE_CREDIT_START_DATE);
};

// TODO FEE-8732: clean up killswitch
export const getIsWithinReturnWindow = ({ orderDate, windowLength, forceStoreCreditStartDate }: isWithinReturnWindowProps) => {
  const parsedOrderDate = dayjs(orderDate.split(' at ')[0]);
  const today = dayjs();
  const BUFFER_DAYS = 1; // accounts for time-zones, gives an extra day for return

  // Leadership Timeline Tracking this date here - https://quip-amazon.com/NPn0ABslTM20/Fellowship-of-the-Store-Credit#temp:C:XaVc8159fab707240fabad84f45f
  const RETURN_STORE_CREDIT_START_DATE = getReturnStoreCreditStartDate({ windowLength, forceStoreCreditStartDate });

  // If today is before start date, assume within window
  if (today.isBefore(RETURN_STORE_CREDIT_START_DATE)) {
    return true;
  }

  // If order date is before start date, assume within window
  if (parsedOrderDate.isBefore(RETURN_STORE_CREDIT_START_DATE)) {
    return true;
  }

  const isWithinReturnWindow = isTodayWithinNDaysAfterDate(parsedOrderDate, windowLength + BUFFER_DAYS);
  return isWithinReturnWindow;
};

export const safeParseOrderDate = (orderDate: string) => {
  const splitDate = orderDate.split(' at ')[0];
  return dayjs(splitDate).isValid() ? parseDate(splitDate) : parseDate('July 12, 1999');
};
